import React, {useEffect, useState} from "react";
import { ModalType } from "common/common-models";
import { EditPartnerModal } from "components/Partners/modals/EditPartnerModal";
import { Partner, PartnerFilters, PartnerSortFields } from "components/Partners/models";
import { PartnersSearchBar } from "components/Partners/PartnersSearchBar";
import { PartnersTable } from "components/Partners/PartnersTable";
import { IPageResponse, SortDirection } from "common/interfaces/IPageResponse";
import { IPageRequest } from "common/interfaces/IPageRequest";
import { PartnersService } from "components/Partners/Partners.service";
import AwesomeDebouncePromise from "awesome-debounce-promise";
import { DeletePartnerConfirmModal } from "components/Partners/modals/DeletePartnerConfirmModal";

interface PartnersProps {
    onLoadValidatePage: () => void;
}

interface ModalState {
    partner?: Partner;
    modalType?: ModalType;
}

const searchApi = AwesomeDebouncePromise(
    request => PartnersService.findPartners(request),
    300
);

const Partners = (props: PartnersProps) => {
    const [partners, setPartners] = useState<IPageResponse<Partner>>();
    const [pageRequest, setPageRequest] = useState<IPageRequest<PartnerSortFields>>({
        page: 0,
        size: 20,
        sortDirection: SortDirection.ASC,
        sortField: PartnerSortFields.ID,
        searchQuery: null
    });
    const [filters, setFilters] = useState<PartnerFilters>({});
    const [isRequesting, setIsRequesting] = useState(false);
    const [deleteModal, setDeleteModal] = useState<ModalState>();
    const [editModal, setEditModal] = useState<ModalState>();

    const searchPartners = (request: any, showLoader: boolean) => {
        if(showLoader) setIsRequesting(true);

        searchApi(request).then((response: IPageResponse<Partner>) => {
            setPartners(response)
            setIsRequesting(false)
        })
    };

    const onPageChange = (targetPage: number) => {
        const page = targetPage - 1;
        setPageRequest({
            ...pageRequest,
            page
        })

        searchPartners({
            page,
            size: pageRequest.size,
            sortDirection: pageRequest.sortDirection,
            sortField: pageRequest.sortField,
            searchQuery: filters?.search
        }, true)
    };

    const handleSortClick = (sortField: PartnerSortFields) => {
        let sortDirection = SortDirection.ASC;
        if (pageRequest.sortField.match(sortField)) {
            sortDirection = pageRequest.sortDirection === SortDirection.DESC ? SortDirection.ASC : SortDirection.DESC;
        }

        setPageRequest({
            ...pageRequest,
            sortDirection,
            sortField
        })

        searchPartners({
            page: pageRequest.page,
            size: pageRequest.size,
            searchQuery: filters?.search,
            sortDirection,
            sortField,
        }, false)
    };

    const handleFilterChange = (change: PartnerFilters) => {
        setPartners(undefined);
        setPageRequest({
            ...pageRequest,
            page: 0
        })
        setFilters({
            ...filters,
            ...change
        })

        searchPartners({
            page: 0,
            size: pageRequest.size,
            sortDirection: pageRequest.sortDirection,
            sortField: pageRequest.sortField,
            searchQuery: change?.search
        }, false)
    };

    const openEditPartnerModal = (modalType: ModalType, partner?: Partner) => {
        setEditModal({
            modalType: modalType,
            partner
        });
    };

    const openDeletePartnerModal = (partner: Partner) => {
        setDeleteModal({
            partner
        });
    };

    const initializeRequests = (isInitial: boolean) => {
        searchPartners({
            page: 0,
            size: pageRequest.size,
            sortDirection: pageRequest.sortDirection,
            sortField: pageRequest.sortField,
            searchQuery: filters?.search
        }, isInitial);
    }

    useEffect(() => {
        props.onLoadValidatePage();
        initializeRequests(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return (
        <div className="container">
            <div className="row">
                <div className="col">
                    <PartnersSearchBar
                        onFilter={handleFilterChange}
                        filters={filters}
                        openPartnerModal={openEditPartnerModal}
                    />
                    <PartnersTable
                        onPageChange={onPageChange}
                        onSort={handleSortClick}
                        partners={partners}
                        sortDirection={pageRequest.sortDirection}
                        sortField={pageRequest.sortField}
                        isRequesting={isRequesting}
                        openEditPartnerModal={openEditPartnerModal}
                        openDeletePartnerModal={openDeletePartnerModal}
                    />
                </div>
            </div>
            {
                editModal && (
                    <EditPartnerModal
                        handleModalHide={() => { setEditModal(undefined) }}
                        modalType={editModal.modalType!}
                        loadPartners={() => initializeRequests(false)}
                        partner={editModal.partner}
                    />
                )
            }
            {
                deleteModal && (
                    <DeletePartnerConfirmModal
                        handleRejectClose={() => {
                            setDeleteModal(undefined)
                        }}
                        handleSubmitClose={() => {
                            setDeleteModal(undefined)
                            initializeRequests(false);
                        }}
                        partner={deleteModal.partner!}
                    />
                )
            }
        </div>
    );
}

export default Partners;
